// src/components/analysis/AnalysisSettings.jsx
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiSearch, HiExclamationCircle, HiInformationCircle, HiCalendar, HiChevronDown } from 'react-icons/hi';
import { buildAnalyzePayload } from './analysisSettingsModel';

const TooltipIcon = ({ text, position = 'left', side = 'top' }) => {
  const bubbleAlign = position === 'right' ? 'right-0' : 'left-0';
  const arrowAlign = position === 'right' ? 'right-1.5' : 'left-1.5';
  const verticalPos = side === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2';
  const arrowStyle = side === 'bottom' ? 'bottom-full border-b-slate-900/95' : 'top-full border-t-slate-900/95';
  
  return (
    <div className="group relative inline-block ml-1.5 cursor-help">
      <HiInformationCircle className="h-3.5 w-3.5 text-slate-500 group-hover:text-blue-400 transition-colors" />
      <div className={`absolute ${verticalPos} ${bubbleAlign} hidden group-hover:block w-48 p-2.5 bg-slate-900/95 backdrop-blur-sm text-[11px] leading-relaxed text-slate-200 rounded-lg shadow-2xl border border-slate-700/50 z-[150] pointer-events-none animate-in fade-in slide-in-from-${side === 'bottom' ? 'top' : 'bottom'}-1 duration-200 whitespace-normal break-words`}>
        {text}
        <div className={`absolute ${arrowStyle} ${arrowAlign} border-[5px] border-transparent`}></div>
      </div>
    </div>
  );
};

const InputField = ({ label, name, value, onChange, type = 'text', readOnly, tooltip, tooltipPosition, tooltipSide }) => (
  <div>
    <div className="flex items-center mb-1.5">
      <label className="text-[13px] font-medium text-slate-300 block">{label}</label>
      {tooltip && <TooltipIcon text={tooltip} position={tooltipPosition} side={tooltipSide} />}
    </div>
    <input
      id={name} name={name} type={type} value={value} onChange={onChange} readOnly={readOnly}
      className={`w-full border border-slate-600/50 rounded-lg text-white p-2.5 text-sm focus:outline-none focus:ring-2 transition-all 
        ${readOnly ? 'bg-slate-800/50 border-slate-700 text-slate-400 cursor-not-allowed' : 'bg-slate-700/50 focus:ring-blue-500/50'}`}
    />
  </div>
);

// Новый компонент для выбора таймфрейма (стилизован под поиск монет)
const CustomSelect = ({ label, value, onChange, options, tooltip, tooltipPosition, tooltipSide }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (opt) => {
    onChange(opt);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="flex items-center mb-1.5">
        <label className="text-[13px] font-medium text-slate-300 block">{label}</label>
        {tooltip && <TooltipIcon text={tooltip} position={tooltipPosition} side={tooltipSide} />}
      </div>
      <div 
        className="relative cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg text-white p-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all select-none">
          {value}m
        </div>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500">
           <HiChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>
      
      {isOpen && (
        <ul className="absolute z-[110] w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl max-h-60 overflow-y-auto custom-scrollbar backdrop-blur-md">
          {options.map((opt) => (
            <li 
              key={opt} 
              onClick={() => handleSelect(opt)} 
              className={`px-4 py-2 text-sm cursor-pointer hover:bg-blue-600 hover:text-white transition-colors text-slate-300 ${String(opt) === String(value) ? 'bg-blue-500/20 text-blue-400' : ''}`}
            >
              {opt}m
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SymbolAutocomplete = ({ label, name, value, onChange, options, placeholder, isLoading, tooltip, tooltipPosition, tooltipSide }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!options) return;
    const term = String(value).toUpperCase().trim();
    setFilteredOptions(!term ? options : options.filter(opt => opt.includes(term)));
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        const term = String(value).toUpperCase().trim();
        if (term && options.length > 0 && !options.includes(term)) {
          onChange({ target: { name, value: '' } });
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options, name, onChange]);

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="flex items-center mb-1.5">
        <label htmlFor={name} className="text-[13px] font-medium text-slate-300 block">{label}</label>
        {tooltip && <TooltipIcon text={tooltip} position={tooltipPosition} side={tooltipSide} />}
      </div>
      <div className="relative">
        <input
          name={name} type="text" value={value}
          onChange={(e) => { onChange({ target: { name, value: e.target.value.toUpperCase() } }); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder={isLoading ? "..." : placeholder}
          autoComplete="off" disabled={isLoading}
          className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg text-white p-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all uppercase placeholder:normal-case placeholder:text-slate-500"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500">
           {isLoading ? <span className="animate-spin h-4 w-4 border-2 border-slate-500 border-t-transparent rounded-full"></span> : <HiSearch className="w-4 h-4" />}
        </div>
      </div>
      {isOpen && options.length > 0 && (
        <ul className="absolute z-[110] w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl max-h-60 overflow-y-auto custom-scrollbar backdrop-blur-md">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <li key={opt} onMouseDown={(e) => { e.preventDefault(); onChange({ target: { name, value: opt } }); setIsOpen(false); }} className={`px-4 py-2 text-sm cursor-pointer hover:bg-blue-600 hover:text-white transition-colors text-slate-300 ${opt === value ? 'bg-blue-500/20 text-blue-400' : ''}`}>{opt}</li>
            ))
          ) : (
            <li className="px-4 py-3 text-xs text-slate-500 flex items-center justify-center"><HiExclamationCircle className="mr-2" /> No symbols</li>
          )}
        </ul>
      )}
    </div>
  );
};

const AnalysisSettings = ({ settings, onSettingsChange, onAnalyze, isLoading }) => {
  const { t } = useTranslation();
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [isLoadingSymbols, setIsLoadingSymbols] = useState(false);

  useEffect(() => {
    const fetchSymbols = async () => {
      setIsLoadingSymbols(true);
      try {
        const token = localStorage.getItem('authToken'); 
        const response = await fetch('https://api.hedger.bar/api/candles/symbols?exchange=Bybit', {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableSymbols(Array.isArray(data) ? data : (data.symbols || []));
        }
      } catch (error) { console.error("Error fetching symbols:", error); } 
      finally { setIsLoadingSymbols(false); }
    };
    fetchSymbols();
  }, []);

  const calculatedPositionSize = Number(settings.margin || 0) * Number(settings.leverage || 0);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === 'checkbox' ? checked : value;
    if (name === 'shortLevels') finalValue = value.replace(/[^0-9,. ]/g, '');
    if (name === 'longLevels') finalValue = value.replace(/[^-0-9,. ]/g, '');
    onSettingsChange((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onAnalyze(buildAnalyzePayload(settings));
  };

  return (
    <div className="h-full bg-slate-800 p-6 rounded-2xl border border-slate-700/50 shadow-xl text-white">
      <h3 className="text-lg font-bold mb-6 tracking-tight">{t('analysis.settings_title')}</h3>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SymbolAutocomplete 
              label={t('analysis.main_coin')} name="symbol_a" value={settings.symbol_a} 
              onChange={handleChange} options={availableSymbols} isLoading={isLoadingSymbols} 
              tooltip={t('strategy_settings.tooltips.symbol')}
              tooltipSide="bottom" 
            />
            
            <CustomSelect 
              label={t('analysis.timeframe')}
              value={settings.timeframe}
              onChange={(val) => handleChange({ target: { name: 'timeframe', value: val } })}
              options={[1, 3, 5, 15, 30, 60]}
              tooltip={t('strategy_settings.tooltips.timeframe')}
              tooltipSide="bottom"
            />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InputField label={t('strategy_settings.zscore')} name="zscore_period" value={settings.zscore_period} onChange={handleChange} type="number" tooltip={t('strategy_settings.tooltips.zscore')} />
          <InputField label={t('strategy_settings.bar_count')} name="bar" value={settings.bar} onChange={handleChange} type="number" tooltip={t('strategy_settings.tooltips.bar_count')} />
          <InputField label={t('strategy_settings.margin')} name="margin" value={settings.margin} onChange={handleChange} type="number" tooltip={t('strategy_settings.tooltips.margin')} />
          <InputField label={t('strategy_settings.leverage')} name="leverage" value={settings.leverage} onChange={handleChange} type="number" tooltip={t('strategy_settings.tooltips.leverage')} tooltipPosition="right" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InputField label={t('analysis.pos_size')} value={calculatedPositionSize} readOnly tooltip={t('strategy_settings.tooltips.pos_size')} />
          <InputField label={t('analysis.comm_rate')} name="commission_rate" value={settings.commission_rate} onChange={handleChange} tooltip={t('strategy_settings.tooltips.commission')} />
          <InputField label={t('strategy_settings.exit')} name="exit" value={settings.exit} onChange={handleChange} type="number" tooltip={t('strategy_settings.tooltips.exit')} tooltipPosition="right" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField label={t('analysis.levels_plus')} name="shortLevels" value={settings.shortLevels} onChange={handleChange} tooltip={t('strategy_settings.tooltips.entry_plus')} />
          <InputField label={t('analysis.levels_minus')} name="longLevels" value={settings.longLevels} onChange={handleChange} tooltip={t('strategy_settings.tooltips.entry_minus')} />
        </div>

        <button type="submit" disabled={isLoading} className="w-full py-4 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded-xl font-black uppercase tracking-widest transition-all shadow-lg active:scale-95">
          {isLoading ? t('analysis.btn_loading') : t('analysis.btn_start')}
        </button>
      </form>
    </div>
  );
};

export default AnalysisSettings;
